import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Card, Container, Button, Spinner, Table } from 'react-bootstrap'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { MEETING_STATUS, RESOLUTION_STATUS, UPLOAD_BASE, fmtDateTH, fmtTime } from './aoShared'

// ประชุมของฉัน + มติที่ฉันรับผิดชอบ — ครูทุกคน
export default function MyMeetings() {
  const [meetings, setMeetings] = useState([])
  const [resolutions, setResolutions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get('/api/ao/my/meetings')
      if (res.data.success) {
        setMeetings(res.data.data.meetings)
        setResolutions(res.data.data.resolutions)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const updateResolution = async (r) => {
    const { value: formValues } = await Swal.fire({
      title: 'อัปเดตผลการดำเนินการ',
      html: `
        <div style="text-align:left;font-size:0.85rem;margin-bottom:6px">${r.ms_text}</div>
        <select id="sw-status" class="form-select form-select-sm mb-2">
          ${Object.entries(RESOLUTION_STATUS).map(([k, v]) =>
            `<option value="${k}" ${k === r.ms_status ? 'selected' : ''}>${v.label}</option>`).join('')}
        </select>
        <textarea id="sw-result" class="form-control form-control-sm" rows="3"
          placeholder="ผลการดำเนินการ">${r.ms_result || ''}</textarea>`,
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ปิด',
      preConfirm: () => ({
        ms_status: document.getElementById('sw-status').value,
        ms_result: document.getElementById('sw-result').value,
      }),
    })
    if (!formValues) return
    try {
      await axiosInstance.put(`/api/ao/my/resolutions/${r.ms_id}`, formValues)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const openResolutions = resolutions.filter(r => r.ms_status !== 'done')

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-team-line me-2 text-primary" />
              การประชุมของฉัน
            </h4>
            {openResolutions.length > 0 && (
              <Badge bg="warning" className="fs-sm">มติค้างดำเนินการ {openResolutions.length} รายการ</Badge>
            )}
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <>
              {resolutions.length > 0 && (
                <Card className="card-one mb-3">
                  <Card.Header><Card.Title as="h6" className="mb-0">มติที่ฉันรับผิดชอบ</Card.Title></Card.Header>
                  <Card.Body className="p-0">
                    <Table responsive hover className="mb-0 fs-sm">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 130 }}>การประชุม</th>
                          <th>มติ</th>
                          <th style={{ width: 110 }}>กำหนดเสร็จ</th>
                          <th style={{ width: 140 }}>สถานะ</th>
                          <th style={{ width: 90 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {resolutions.map(r => {
                          const rs = RESOLUTION_STATUS[r.ms_status] || RESOLUTION_STATUS.pending
                          return (
                            <tr key={r.ms_id}>
                              <td>
                                ครั้งที่ {r.mt_full_no}
                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>{fmtDateTH(r.mt_date)}</div>
                              </td>
                              <td>
                                {r.ms_text}
                                {r.ms_result && <div className="text-muted" style={{ fontSize: '0.75rem' }}>ผล: {r.ms_result}</div>}
                              </td>
                              <td>{fmtDateTH(r.ms_due)}</td>
                              <td><Badge bg={rs.bg}>{rs.label}</Badge></td>
                              <td className="text-end">
                                <Button size="sm" variant="outline-primary" onClick={() => updateResolution(r)}>
                                  อัปเดตผล
                                </Button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}

              <Card className="card-one">
                <Card.Header><Card.Title as="h6" className="mb-0">ประชุมที่ฉันถูกเชิญ</Card.Title></Card.Header>
                <Card.Body className="p-0">
                  {meetings.length === 0 ? (
                    <div className="text-center text-muted py-4">ยังไม่มีการประชุมที่คุณถูกเชิญ</div>
                  ) : (
                    <Table responsive hover className="mb-0 fs-sm">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 90 }}>ครั้งที่</th>
                          <th>ชื่อการประชุม</th>
                          <th style={{ width: 180 }}>วัน-เวลา</th>
                          <th>สถานที่</th>
                          <th style={{ width: 100 }}>สถานะ</th>
                          <th style={{ width: 110 }}>เอกสาร</th>
                        </tr>
                      </thead>
                      <tbody>
                        {meetings.map(m => {
                          const st = MEETING_STATUS[m.mt_status] || MEETING_STATUS.scheduled
                          return (
                            <tr key={m.mi_id}>
                              <td className="fw-semibold">{m.mt_full_no}</td>
                              <td>{m.mt_title}</td>
                              <td>{fmtDateTH(m.mt_date)}{m.mt_time_start ? ` ${fmtTime(m.mt_time_start)}` : ''}</td>
                              <td>{m.mt_place || '-'}</td>
                              <td><Badge bg={st.bg}>{st.label}</Badge></td>
                              <td>
                                {m.mt_invite_file && (
                                  <a className="me-2" href={`${UPLOAD_BASE}/uploads/ao_docs/${m.mt_invite_file}`}
                                    target="_blank" rel="noreferrer" title="หนังสือเชิญ">
                                    <i className="ri-mail-line" /> เชิญ
                                  </a>
                                )}
                                {m.mt_report_file && (
                                  <a href={`${UPLOAD_BASE}/uploads/ao_docs/${m.mt_report_file}`}
                                    target="_blank" rel="noreferrer" title="รายงานการประชุม">
                                    <i className="ri-file-text-line" /> รายงาน
                                  </a>
                                )}
                                {!m.mt_invite_file && !m.mt_report_file && '-'}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
